import React, { useRef } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaGoogle, FaStar, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { AiFillCheckCircle } from 'react-icons/ai';

const ReviewsSection = styled.section`
  padding: 80px 0;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: hidden;
`;

const TitleWrapper = styled.div`
  text-align: center;
  margin-bottom: 60px;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 200px;
    height: 1px;
    background: #e0e0e0;
  }
`;

const Title = styled.h2`
  font-size: 2.5rem;
  color: #1A365D;
  margin-bottom: 30px;
  font-family: 'Playfair Display', serif;

  @media screen and (max-width: 480px) {
    font-size: 2rem;
  }
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  display: flex;
  align-items: center;
  gap: 40px;
  padding: 0 20px;

  @media (max-width: 900px) {
    flex-direction: column;
    text-align: center;
  }
`;

const SummaryColumn = styled.div`
  flex: 0 0 250px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const SummaryHeading = styled.h3`
  font-family: 'Arial', sans-serif;
  font-weight: 900;
  font-size: 1.8rem;
  margin-bottom: 10px;
  color: #000;
`;

const StarsWrapper = styled.div`
  display: flex;
  color: #fbbc05;
  font-size: 1.5rem;
  margin-bottom: 10px;
`;

const SummaryText = styled.p`
  font-family: 'Arial', sans-serif;
  font-size: 1rem;
  color: #555;
  margin-bottom: 15px;

  strong {
    color: #000;
  }
`;

const GoogleLogoWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 2rem;
  font-family: 'Product Sans', Arial, sans-serif;
  font-weight: bold;
`;

const Letter = styled.span`
  color: ${props => props.color};
`;

const CarouselWrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  position: relative;
  width: 100%;
`;

const ReviewCardsContainer = styled.div`
  display: flex;
  gap: 20px;
  width: 100%;
  overflow-x: auto;
  padding: 20px 5px;
  scroll-behavior: smooth;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const ReviewCard = styled(motion.div)`
  background: #f5f5f5;
  border-radius: 12px;
  padding: 24px;
  min-width: 320px;
  flex: 0 0 320px;
  position: relative;
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);

  @media (max-width: 480px) {
    min-width: 280px;
    flex: 0 0 280px;
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 15px;
`;

const AuthorSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ProfileImg = styled.div`
  width: 45px;
  height: 45px;
  border-radius: 50%;
  background: #1A365D;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 0.9rem;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const AuthorDetails = styled.div`
  display: flex;
  flex-direction: column;
`;

const AuthorName = styled.span`
  font-weight: bold;
  color: #000;
  font-size: 0.95rem;
`;

const ReviewDate = styled.span`
  font-size: 0.75rem;
  color: #777;
`;

const GoogleIconSmall = styled(FaGoogle)`
  color: #EA4335;
  font-size: 1.2rem;
`;

const StarRatingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 15px;
`;

const VerifiedBadge = styled(AiFillCheckCircle)`
  color: #3b82f6;
  font-size: 1.1rem;
`;

const ReviewText = styled.p`
  font-size: 0.9rem;
  line-height: 1.5;
  color: #333;
  margin-bottom: 15px;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const MoreInfo = styled.a`
  font-size: 0.85rem;
  color: #777;
  text-decoration: none;
  cursor: pointer;
  
  &:hover {
    text-decoration: underline;
  }
`;

const NavButton = styled.button`
  background: white;
  border: 1px solid #ddd;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  z-index: 10;
  box-shadow: 0 2px 5px rgba(0,0,0,0.1);

  &.left {
    left: -18px;
  }

  &.right {
    right: -18px;
  }

  &:hover {
    background: #f9f9f9;
  }

  @media (max-width: 900px) {
    display: none;
  }
`;

const TrustindexBadge = styled.div`
  align-self: flex-end;
  background: #006633;
  color: white;
  padding: 6px 14px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: bold;
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 30px;
  margin-right: 20px;
  cursor: pointer;

  i {
    font-style: normal;
    background: white;
    color: #006633;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.6rem;
  }
`;

const GoogleWord = () => (
    <GoogleLogoWrapper>
        <Letter color="#4285F4">G</Letter>
        <Letter color="#EA4335">o</Letter>
        <Letter color="#FBBC05">o</Letter>
        <Letter color="#4285F4">g</Letter>
        <Letter color="#34A853">l</Letter>
        <Letter color="#EA4335">e</Letter>
    </GoogleLogoWrapper>
);

const reviewsData = [
    {
        id: 1,
        name: 'David Edições',
        date: 'Fevereiro 2026',
        text: 'Gostaria de agradecer ao Crematório Dom José pelo profundo respeito e cuidado em um momento tão delicado. O espaço para reflexão é impecável, transmitindo uma paz necessária e permitindo uma despedida digna e serena. Cada detalhe, como a iluminação e o ambiente silencioso, faz toda a diferença para o conforto das famílias.',
        initials: 'DE',
        link: 'https://maps.app.goo.gl/ZW27hPM4EpcumHxd7'
    },
    {
        id: 2,
        name: 'Márcia Regina Schulz',
        date: 'Fevereiro 2026',
        text: 'Num dos piores momentos da nossa família. A despedida repentina de meu pai. Todos os profissionais que nos atenderam do proprietário  Seu José aos colaboradores ( cuidados com o corpo atendimento no crematório)foi de grandiosidade e respeito. Muito acolhimento e cuidado conosco e com nosso pai que partiu. Cada detalhe foi nos dado com amor, carinho e dedicação.',
        initials: 'MS',
        link: 'https://maps.app.goo.gl/ZauUzUTd5D6eW5mT9'
    },
    {
        id: 3,
        name: 'Dioneia pech',
        date: 'Janeiro 2025',
        text: 'Excelente! Um lugar que transmite paz e tranquilidade! Atendimento de alto nível!',
        initials: 'DP',
        link: 'https://maps.app.goo.gl/TQhb8nRrjCLY3Ac98'
    },
    {
        id: 4,
        name: 'Bia Brixner',
        date: 'Outubro 2025',
        text: 'Referencia de estrutura e acolhimento. Tantas histórias de gratidão pelo excelente atendimento.Sigam firmes no propósito de vocês. Sigam crescendo e cuidando da dor das famílias com cuidado e amor.',
        initials: 'BB',
        link: 'https://maps.app.goo.gl/Q2kJXPb7qFfxWmma8'
    },
    {
        id: 5,
        name: 'Joice Kruger',
        date: 'Outubro 2025',
        text: 'Fomos atendidos com dignidade,cerimônia muito bonita,muito além do esperado!Atendimento perfeito!!!',
        initials: 'JK',
        link: 'https://maps.app.goo.gl/tZbbYyCzp1Dhn3Ju5'
    }
];

const GoogleReviews = () => {
    const carouselRef = useRef(null);

    const scroll = (direction) => {
        if (carouselRef.current) {
            const { scrollLeft } = carouselRef.current;
            const scrollTo = direction === 'left' ? scrollLeft - 340 : scrollLeft + 340;
            carouselRef.current.scrollTo({ left: scrollTo, behavior: 'smooth' });
        }
    };

    return (
        <ReviewsSection id="avaliacoes">
            <TitleWrapper>
                <Title>Confira as avaliações de nossos clientes</Title>
            </TitleWrapper>

            <ContentContainer>
                <SummaryColumn>
                    <SummaryHeading>EXCELENTE</SummaryHeading>
                    <StarsWrapper>
                        <FaStar />
                        <FaStar />
                        <FaStar />
                        <FaStar />
                        <FaStar />
                    </StarsWrapper>
                    <SummaryText>Com base em <strong>30 avaliações</strong></SummaryText>
                    <GoogleWord />
                </SummaryColumn>

                <CarouselWrapper>
                    <NavButton className="left" onClick={() => scroll('left')} aria-label="Anterior"><FaChevronLeft color="#777" /></NavButton>

                    <ReviewCardsContainer ref={carouselRef}>
                        {reviewsData.map((review) => (
                            <ReviewCard
                                key={review.id}
                                whileHover={{ y: -5 }}
                                transition={{ type: 'spring', stiffness: 300 }}
                            >
                                <CardHeader>
                                    <AuthorSection>
                                        <ProfileImg>{review.initials}</ProfileImg>
                                        <AuthorDetails>
                                            <AuthorName>{review.name}</AuthorName>
                                            <ReviewDate>{review.date}</ReviewDate>
                                        </AuthorDetails>
                                    </AuthorSection>
                                </CardHeader>

                                <StarRatingRow>
                                    <StarsWrapper style={{ fontSize: '1rem', marginBottom: 0 }}>
                                        <FaStar /><FaStar /><FaStar /><FaStar /><FaStar />
                                    </StarsWrapper>
                                    <VerifiedBadge title="Avaliação Verificada" />
                                </StarRatingRow>

                                <ReviewText>{review.text}</ReviewText>
                                <MoreInfo href={review.link} target="_blank" rel="noopener noreferrer">Ver no Google</MoreInfo>
                            </ReviewCard>
                        ))}
                    </ReviewCardsContainer>

                    <NavButton className="right" onClick={() => scroll('right')} aria-label="Próximo"><FaChevronRight color="#777" /></NavButton>
                </CarouselWrapper>
            </ContentContainer>

        </ReviewsSection>
    );
};

export default GoogleReviews;
